class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

const SITE_NAV_INITIAL_OPEN_DELAY = 150;
const SITE_NAV_HOVER_INTENT_DELAY = 250;

class SiteNav extends HTMLElement {
  constructor() {
    super();

    this.onMegaItemEnter = this.onMenuItemEnter.bind(this);
    this.onMegaItemLeave = this.onMenuItemLeave.bind(this);
    this.onNonMegaItemEnter = this.closeMegaDropdowns.bind(this, true);
    this.onNavLeave = () => this.closeMegaDropdowns();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.nonMegaItems = this.querySelectorAll(
      ".f-site-nav__item:not(.f-site-nav__item--mega)"
    );
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.pendingMegaItem = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.addEventListener("mouseenter", this.onMegaItemEnter);
          megaItem.addEventListener("mouseleave", this.onMegaItemLeave);
        });
      this.addEventListener("mouseleave", this.onNavLeave);
      this.nonMegaItems &&
        this.nonMegaItems.forEach((nonMegaItem) => {
          nonMegaItem.addEventListener("mouseenter", this.onNonMegaItemEnter);
        });
    }
  }

  get activeMegaItem() {
    return [...this.megaItems].find((megaItem) =>
      megaItem.classList.contains(this.classes.itemActive)
    );
  }

  clearHoverTimeouts() {
    clearTimeout(this.timeoutEnter);
    clearTimeout(this.timeoutLeave);
  }

  clearHoverState() {
    this.clearHoverTimeouts();
    this.pendingMegaItem = null;
    this.setActiveMegaItem();
  }

  getSiblingNavs() {
    return [...this.header.querySelectorAll("site-nav")].filter(
      (siteNav) => siteNav !== this
    );
  }

  clearSiblingHoverState() {
    this.getSiblingNavs().forEach((siteNav) => {
      siteNav.clearHoverState();
    });
  }

  hasActiveHeaderMegaItem() {
    return [...this.header.querySelectorAll("site-nav")].some(
      (siteNav) => siteNav.activeMegaItem
    );
  }

  activateMegaItem(target) {
    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (!dropdown) return;

    this.pendingMegaItem = null;
    this.clearSiblingHoverState();
    this.setActiveMegaItem(target);

    this.header.handleMegaItemActive(dropdown);
  }

  setActiveMegaItem(target = null) {
    this.megaItems.forEach((megaItem) => {
      megaItem.classList.toggle(this.classes.itemActive, megaItem === target);
    });
  }

  onMenuItemEnter(evt) {
    this.clearHoverTimeouts();

    const { currentTarget } = evt;
    const dropdown = currentTarget.querySelector(".f-site-nav__dropdown");

    if (!dropdown) {
      this.closeMegaDropdowns(true);
      return;
    }

    const isCurrentItemActive = currentTarget.classList.contains(
      this.classes.itemActive
    );

    if (isCurrentItemActive) {
      this.pendingMegaItem = null;
      return;
    }

    const delay = this.activeMegaItem
      ? SITE_NAV_HOVER_INTENT_DELAY
      : SITE_NAV_INITIAL_OPEN_DELAY;

    this.pendingMegaItem = currentTarget;
    this.timeoutEnter = setTimeout(() => {
      if (this.pendingMegaItem === currentTarget) {
        this.activateMegaItem(currentTarget);
      }
    }, delay);
  }

  onMenuItemLeave(evt) {
    const { currentTarget } = evt;
    const isPendingMegaItem = currentTarget === this.pendingMegaItem;

    clearTimeout(this.timeoutEnter);
    if (isPendingMegaItem) {
      this.pendingMegaItem = null;
    }

    this.timeoutLeave = setTimeout(() => {
      if (isPendingMegaItem) {
        this.setActiveMegaItem();
        this.header.handleMegaItemDeactive();
        return;
      }

      if (currentTarget.classList.contains(this.classes.itemActive)) {
        currentTarget.classList.remove(this.classes.itemActive);
      }

      if (!this.activeMegaItem) {
        if (!this.hasActiveHeaderMegaItem()) {
          this.header.handleMegaItemDeactive();
        }
      }
    }, SITE_NAV_HOVER_INTENT_DELAY);
  }

  closeMegaDropdowns(immediate = false) {
    this.clearHoverState();

    if (immediate) {
      this.clearSiblingHoverState();
      this.header.handleMegaItemDeactive();
      return;
    }

    this.timeoutLeave = setTimeout(() => {
      if (!this.hasActiveHeaderMegaItem()) {
        this.header.handleMegaItemDeactive();
      }
    }, SITE_NAV_HOVER_INTENT_DELAY);
  }

  disconnectedCallback() {
    this.clearHoverTimeouts();

    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.removeEventListener("mouseenter", this.onMegaItemEnter);
        megaItem.removeEventListener("mouseleave", this.onMegaItemLeave);
      });
    this.removeEventListener("mouseleave", this.onNavLeave);
    this.nonMegaItems &&
      this.nonMegaItems.forEach((nonMegaItem) => {
        nonMegaItem.removeEventListener("mouseenter", this.onNonMegaItemEnter);
      });
  }
}
customElements.define("site-nav", SiteNav);
